import Button, { ButtonProps } from "@components/buttons/Button";
import Pdf from "@components/other/Pdf";
import { pdf } from "@react-pdf/renderer";
import { Dropdown } from "antd";
import { saveAs } from "file-saver";
import React from "react";
import {
  exportObjectsAsCsv,
  exportObjectsAsExcel,
  extractDataRows,
  resolveColumns,
} from "./utils";

export type ExportColumn<
  TRow extends Record<string, any> = Record<string, any>
> = {
  // property to read from each row
  dataIndex: string;
  // header label
  title: string;
  // optional transform of the raw value for export
  formatter?: (value: any, row: TRow, rowIndex: number) => any;
  // optional flag to exclude from export
  excludeFromExport?: boolean;
};

export type ExportColumnsInput<
  TRow extends Record<string, any> = Record<string, any>
> = Array<string | ExportColumn<TRow>> | undefined;

export type ExportButtonProps<
  TRow extends Record<string, any> = Record<string, any>
> = {
  // Array of objects to export.
  rows?: readonly TRow[];
  // Optional dynamic loader. If provided, it will be called on click to fetch rows.
  resolveRows?: () => Promise<readonly TRow[]> | readonly TRow[];
  // Optional columns; if omitted, columns are inferred from the union of row keys.
  // Example shape:
  // [
  //   { dataIndex: "name", title: "Customer Name" },
  //   { dataIndex: "email", title: "Email" },
  //   { dataIndex: "phoneNumber", title: "Phone" },
  // ]
  columns?: ExportColumnsInput<TRow>;
  // File name without extension (e.g., "customers")
  filename: string;
  // Title for the export (used for workbook title and PDF title)
  title?: string;
  // Creator/producer for PDF metadata
  creator?: string;
  // Button label (defaults to "Export")
  label?: React.ReactNode;
  // Toggle formats
  enableCsv?: boolean;
  enableExcel?: boolean;
  enablePdf?: boolean;
  // Error callback when resolveRows throws/rejects
  onResolveError?: (error: unknown) => void;
} & ButtonProps;

/**
 * To export data that you already have, pass in your data to the `rows` prop. To export data that you want to fetch JIT, pass your fetch function into the `resolveRows` prop.
 * NOTE: When using Relay in `resolveRows` to fetch data JIT such as with a mutation, you must return your mutation inside a Promise.
 */
const ExportButton = <TRow extends Record<string, any>>({
  rows,
  resolveRows,
  columns,
  filename,
  title = filename,
  creator,
  label = "Export",
  enableCsv = true,
  enableExcel = true,
  enablePdf = true,
  disabled,
  variant = "text",
  onResolveError,
  ...rest
}: ExportButtonProps<TRow>) => {
  const [isExporting, setIsExporting] = React.useState(false);

  const loadRows = React.useCallback(async (): Promise<readonly TRow[]> => {
    try {
      if (typeof resolveRows === "function") {
        const result = await Promise.resolve(resolveRows());
        if (!Array.isArray(result)) {
          throw new Error(
            "resolveRows must return an array of rows or a Promise that resolves to an array of rows."
          );
        }
        return result as readonly TRow[];
      }
      return (rows ?? []) as readonly TRow[];
    } catch (e) {
      onResolveError?.(e);
      throw e;
    }
  }, [resolveRows, rows, onResolveError]);

  const onCsv = React.useCallback(async () => {
    setIsExporting(true);
    try {
      const latestRows = await loadRows();
      exportObjectsAsCsv(filename, latestRows, columns, title);
    } catch (e) {
      // Error handled by onResolveError
    } finally {
      setIsExporting(false);
    }
  }, [columns, filename, loadRows, title]);

  const onExcel = React.useCallback(async () => {
    setIsExporting(true);
    try {
      const latestRows = await loadRows();
      exportObjectsAsExcel(filename, latestRows, columns, title);
    } catch (e) {
      // Error handled by onResolveError
    } finally {
      setIsExporting(false);
    }
  }, [columns, filename, loadRows, title]);

  const onPdf = React.useCallback(async () => {
    setIsExporting(true);
    try {
      const latestRows = await loadRows();
      const cols = resolveColumns(latestRows, columns);
      const totalColumns = cols.length || 1;
      const widthPercentages = cols.map(() => 100 / totalColumns);

      // Ensure all PDF cell values are strings to avoid renderer issues
      const dataForPdf = extractDataRows(latestRows, cols).map((row) =>
        row.map((cell) => (cell == null ? "" : String(cell)))
      );

      const blob = await pdf(
        <Pdf
          title={title ?? ""}
          columnTitles={cols.map((c) => c.title)}
          data={dataForPdf}
          widthPercentages={widthPercentages}
          creator={creator ?? ""}
        />
      ).toBlob();

      saveAs(blob, `${filename}.pdf`);
    } catch (e) {
      // Error handled by onResolveError
    } finally {
      setIsExporting(false);
    }
  }, [columns, creator, filename, loadRows, title]);

  const menuItems = [
    enableCsv && {
      key: "csv",
      label: (
        <span style={{ color: "inherit", fontWeight: "normal" }}>CSV</span>
      ),
    },
    enableExcel && {
      key: "excel",
      label: (
        <span style={{ color: "inherit", fontWeight: "normal" }}>Excel</span>
      ),
    },
    enablePdf && {
      key: "pdf",
      label: (
        <span style={{ color: "inherit", fontWeight: "normal" }}>PDF</span>
      ),
    },
  ].filter(Boolean) as NonNullable<unknown>[] as {
    key: string;
    label: React.ReactNode;
  }[];

  return (
    <Dropdown
      menu={{
        items: menuItems,
        onClick: async ({ key }) => {
          if (key === "csv") return onCsv();
          if (key === "excel") return onExcel();
          if (key === "pdf") return onPdf();
        },
      }}
      trigger={["click"]}
      disabled={disabled || isExporting}
    >
      <Button variant={variant} disabled={disabled || isExporting} {...rest}>
        {label}
      </Button>
    </Dropdown>
  );
};

export default ExportButton;
